import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { AreaOfOperationCodes } from '../../../../shared/constants/area-of-operation-codes';
import { ScheduleDStepOneService } from '../../components/schedule-d-step-one/services/schedule-d-step-one/schedule-d-step-one.service';
import { ScheduleDStepThreeService } from '../../components/schedule-d-step-three/services/schedule-d-step-three/schedule-d-step-three.service';
import { ScheduleDStepTwoService } from '../../components/schedule-d-step-two/services/schedule-d-step-two/schedule-d-step-two.service';
import { ScheduleDFormModel } from '../../models/schedule-d-form-model';
import { AreaOfOperationCode } from '../../types/area-of-operation-code';

@Injectable({
    providedIn: 'root',
})
export class ScheduleDStepHandlerService {
    private readonly stepOneService = inject(ScheduleDStepOneService);
    private readonly stepTwoService = inject(ScheduleDStepTwoService);
    private readonly stepThreeService = inject(ScheduleDStepThreeService);

    private readonly FIRST_STEP: number = 1;
    private readonly LAST_STEP: number = 3;
    private readonly STEP_RANGE: number[] = Array.from(
        { length: this.LAST_STEP },
        (_, i) => i + 1,
    );

    getFirstStep(): number {
        return this.FIRST_STEP;
    }

    getLastStep(): number {
        return this.LAST_STEP;
    }

    getStepRange(): number[] {
        return this.STEP_RANGE;
    }

    hasStepTwo(form: FormGroup<FormGroupType<ScheduleDFormModel>>): boolean {
        const areaOfOperationCodesWithNoStepTwo: AreaOfOperationCode[] = [
            AreaOfOperationCodes.nationwide,
            AreaOfOperationCodes.continentalUS,
        ];

        return !areaOfOperationCodesWithNoStepTwo.includes(
            form.controls.areaOfOperationCode.value as AreaOfOperationCode,
        );
    }

    isStepValid(
        step: number,
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): Observable<boolean> {
        switch (step) {
            case 1:
                return this.stepOneService.isStepValid(form);
            case 2:
                return this.stepTwoService.isStepValid(form);
            case 3:
                return this.stepThreeService.isStepValid(form);
            default:
                throw new Error(
                    `Step ${step} is not mapped to a service in ${nameOf<ScheduleDStepHandlerService>('isStepValid')}.`,
                );
        }
    }

    updateFormControls(
        step: number,
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): void {
        switch (step) {
            case 1:
                return this.stepOneService.updateFormControls(form);
            case 2:
                return this.stepTwoService.updateFormControls(form);
            case 3:
                return this.stepThreeService.updateFormControls(form);
            default:
                throw new Error(
                    `Step ${step} is not mapped to a service in ${nameOf<ScheduleDStepHandlerService>('updateFormControls')}.`,
                );
        }
    }
}
